import { useState, type ReactNode } from 'react';
import { Drawer, Fact, Facts } from '@/components/ui';
import type { AuditEventView } from '@/types/api';
import { formatDateTime } from '@/utils/format';
import { auditActionLabel, AUDIT_ENTITY_LABELS, label } from '@/utils/labels';

/** One audit record in full: who, from where, and what changed. */
export function AuditDrawer({
  event,
  onClose,
}: {
  readonly event: AuditEventView | null;
  readonly onClose: () => void;
}): ReactNode {
  // Held after `event` goes null so the closing animation has something to draw.
  const [last, setLast] = useState<AuditEventView | null>(null);
  if (event && event !== last) setLast(event);

  const data = event ?? last;
  if (!data) return null;

  const hasDiff = data.diff !== null && typeof data.diff === 'object' && Object.keys(data.diff).length > 0;

  return (
    <Drawer
      open={event !== null}
      title={auditActionLabel(data.action)}
      description={formatDateTime(data.createdAt)}
      onClose={onClose}
      width="md"
    >
      <div className="stack stack-loose">
        <Facts>
          <Fact label="Кто">{data.actorLabel ?? 'система'}</Fact>
          <Fact label="Исход">
            {data.result === 'success' ? 'успешно' : 'отказ'}
            {data.errorCode ? <span className="mono tone-down"> · {data.errorCode}</span> : null}
          </Fact>
          <Fact label="Объект">
            {label(AUDIT_ENTITY_LABELS, data.entityType)}
            {data.entityId ? <span className="mono text-sm muted"> {data.entityId}</span> : null}
          </Fact>
          <Fact label="Код действия">
            <span className="mono">{data.action}</span>
          </Fact>
          <Fact label="Адрес">
            <span className="mono">{data.ip ?? '—'}</span>
          </Fact>
          <Fact label="Браузер">
            <span className="text-sm muted">{data.userAgent || '—'}</span>
          </Fact>
          {data.correlationId ? (
            <Fact label="Запрос">
              <span className="mono text-sm">{data.correlationId}</span>
            </Fact>
          ) : null}
        </Facts>

        {hasDiff ? (
          <div className="stack stack-tight">
            <span className="field-label">Что изменилось</span>
            {/* Secrets were masked when the record was written, not here: a
                value that reached this screen was never stored in the clear. */}
            <pre className="code-block">{JSON.stringify(data.diff, null, 2)}</pre>
          </div>
        ) : null}
      </div>
    </Drawer>
  );
}
